import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Package, Search, X } from 'lucide-react';
import { productsAPI } from '../utils/api';
import { Prodotto } from '../types';

/**
 * Shared query for a single product, cached by id.
 * Used by the combobox itself and by consumers that need the
 * selected product's details (e.g. summary cards).
 */
export function useProdotto(id?: string) {
  return useQuery({
    queryKey: ['prodotto', id],
    queryFn: async () => {
      const res = await productsAPI.getProduct(id!);
      return res.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

interface ProductSearchComboboxProps {
  value: string;
  onChange: (id: string, product: Prodotto | null) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

function ProductThumb({ product, size = 'md' }: { product?: Prodotto; size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  if (product?.immagine_default_url) {
    return (
      <img
        src={product.immagine_default_url}
        alt=""
        className={`${cls} shrink-0 rounded-md border border-gray-200 object-cover`}
      />
    );
  }
  return (
    <span
      className={`${cls} flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50`}
    >
      <Package className="h-4 w-4 text-gray-400" />
    </span>
  );
}

function productMeta(p?: Prodotto | null) {
  if (!p) return '';
  return [p.codice && `Cod. ${p.codice}`, p.marca, p.categoria].filter(Boolean).join(' · ');
}

/**
 * Combobox con ricerca server-side su GET /prodotti?search=...
 * Non carica mai l'intero catalogo: interroga il backend in modo
 * debounced mentre l'utente digita.
 */
export default function ProductSearchCombobox({
  value,
  onChange,
  disabled,
  required,
  placeholder = 'Cerca per nome, codice o descrizione…',
}: ProductSearchComboboxProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: selectedProduct } = useProdotto(value || undefined);

  // Debounce dell'input di ricerca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Ricerca server-side (solo a dropdown aperto)
  const { data: results, isFetching } = useQuery({
    queryKey: ['products-picker', debouncedSearch],
    queryFn: async () => {
      const res = await productsAPI.getProducts({
        search: debouncedSearch || undefined,
        limit: 20,
        is_attivo: true,
      });
      return res.data.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
    enabled: isOpen && !disabled,
  });

  // Evidenzia il primo risultato a ogni nuova lista
  useEffect(() => {
    setHighlightIndex(results?.length ? 0 : -1);
  }, [results]);

  // Chiudi al click fuori
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mantieni visibile l'opzione evidenziata
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      (listRef.current.children[highlightIndex] as HTMLElement | undefined)?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightIndex]);

  const selectProduct = (p: Prodotto) => {
    // Popola la cache così chi legge il prodotto altrove non rifà la fetch
    queryClient.setQueryData(['prodotto', p.id], p);
    onChange(p.id, p);
    setSearch('');
    setIsOpen(false);
  };

  const clearSelection = () => {
    onChange('', null);
    setSearch('');
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
      return;
    }
    if (!isOpen || !results?.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = results[highlightIndex >= 0 ? highlightIndex : 0];
      if (p) selectProduct(p);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Stato "selezionato": card compatta con dettagli prodotto
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2.5">
        <ProductThumb product={selectedProduct} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">
            {selectedProduct?.nome || 'Prodotto selezionato'}
          </p>
          <p className="truncate text-xs text-gray-500">
            {productMeta(selectedProduct) || 'Caricamento dettagli…'}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={clearSelection}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
            aria-label="Rimuovi prodotto selezionato"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
      />
      {isFetching && (
        <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
      )}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          {!results ? (
            <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Caricamento prodotti…
            </div>
          ) : results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-gray-500">
              Nessun prodotto trovato{debouncedSearch ? ` per "${debouncedSearch}"` : ''}.
            </p>
          ) : (
            <ul ref={listRef} className="max-h-64 overflow-auto py-1">
              {results.map((p, idx) => (
                <li
                  key={p.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectProduct(p);
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${
                    idx === highlightIndex ? 'bg-blue-50' : ''
                  }`}
                >
                  <ProductThumb product={p} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{p.nome}</p>
                    <p className="truncate text-xs text-gray-500">{productMeta(p)}</p>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-gray-500">
                    € {Number(p.prezzo_unitario).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
